import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect, useDispatch, type ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import moment, { type Moment } from 'moment';
import { Form } from 'antd';
import * as actions from 'actions';
import { notification, NOTIFICATION_TYPES, Popup } from '_atoms';
import { FormCreateEdit, PopupCandidateDuplicate } from '_molecules';
import { CandidateCreationForm } from '_organisms';
import { BreadCrumbs } from '_atoms/BreadCrumbs';
import history from 'helpers/history';
import { MixpanelService } from 'helpers/Mixpanel';
import { levels } from 'constants/constants';
import { ROUTES } from 'router/constants';
import { clearCandidateData, setDuplicateCandidates } from 'store/reducers/candidateReducer';
import type { RootState } from 'store';
import type { CandidateData, DuplicateCandidate } from 'store/reducers/candidateReducer';
import { formDataMapper } from '_organisms/CandidateCreationForm/utyls';
import { EMPTY_CANDIDATE_DATA, SCROLL_BAR_CANDIDATE } from './constants';
import { uploadDocs } from './utils';
import './style.scss';

type CandidateDuplicateEntry = DuplicateCandidate & { phone: string[]; email: string[] };

interface OwnProps {
  isSSC?: boolean;
}

interface SelectLike {
  value?: string;
  label?: string;
}

interface ExperienceFormItem {
  worked_from?: Moment;
  worked_till?: Moment;
  company?: Array<SelectLike & { value?: string; label?: string }>;
  position?: SelectLike[];
  [key: string]: unknown;
}

interface SpecialityFormItem {
  level?: SelectLike;
  speciality?: SelectLike[];
}

type FormValues = Record<string, unknown> & {
  experience?: ExperienceFormItem[];
  specialities?: SpecialityFormItem[];
  country?: unknown;
  state?: unknown;
  city?: SelectLike[];
  description?: string;
  description_initial?: string;
  first_name_latin?: string;
  last_name_latin?: string;
  first_name_local?: string;
  last_name_local?: string;
  phone?: string[];
  email?: string[];
  uuid?: string;
  cvs?: unknown[];
  sources?: Array<{ is_main?: boolean; [key: string]: unknown }>;
};

const mapStateToProps = (state: RootState) => ({
  candidateDuplicates: state.candidate.candidateDuplicates,
});

const mapDispatchToProps = (dispatch: Parameters<typeof bindActionCreators>[1]) => ({
  getForeignLanguages: bindActionCreators(actions.getForeignLanguages, dispatch),
  getCountries: bindActionCreators(actions.getCountries, dispatch),
  getSpecialties: bindActionCreators(actions.getSpecialties, dispatch),
  getCompanies: bindActionCreators(actions.getCompanies, dispatch),
  getListLocations: bindActionCreators(actions.getListLocations, dispatch),
  getPositions: bindActionCreators(actions.getPositions, dispatch),
  getTags: bindActionCreators(actions.getTags, dispatch),
  saveCandidate: bindActionCreators(actions.saveCandidate, dispatch),
  clearCandidateData: bindActionCreators(clearCandidateData, dispatch),
  checkDuplicate: bindActionCreators(actions.checkDuplicate, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = OwnProps & PropsFromRedux;

const CandidatesCreation = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: candidateId } = useParams<{ id?: string }>();
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openDuplicatePopup, setOpenDuplicatePopup] = useState(false);
  const [duplicate, setDuplicate] = useState<Record<string, unknown>>({});

  useEffect(() => {
    props.getForeignLanguages();
    props.getCountries();
    props.getCompanies({});
    props.getListLocations();
    props.getPositions();
    props.getTags();
    props.getSpecialties();
    const mixpanel = new MixpanelService();
    if (candidateId) {
      mixpanel.track('View Page.Edit Candidate', {
        'Candidate ID': candidateId,
      });
    } else {
      mixpanel.track('View Page.Create Candidate');
    }
    return () => {
      props.clearCandidateData();
    };
  }, []);

  const saveCandidate = async (values: FormValues) => {
    setLoading(true);
    dispatch(setDuplicateCandidates([]));
    try {
      const data = formDataMapper(values) as FormValues & { cvs: unknown[]; sources: FormValues['sources'] };
      const cvs = await uploadDocs(data.cvs as Parameters<typeof uploadDocs>[0]);
      if (cvs === null) return;
      data.cvs = cvs;
      data.sources = data.sources.map(s => ({ ...s, is_main: s.is_main || false }));
      const save = props.saveCandidate as unknown as (
        body: unknown,
        id?: string,
        isSSC?: boolean
      ) => Promise<CandidateData | null | undefined>;
      const res = await save(data, candidateId, props.isSSC);
      if (res) {
        history.push(`/${props.isSSC ? 'self-submitted-' : ''}candidates/${res.uuid}/show`);
        notification({
          message: `Candidate profile has been ${candidateId ? 'updated' : 'created'}.`,
          type: NOTIFICATION_TYPES.SUCCESS,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onOkConfirmation = () => {
    if (props.isSSC) {
      history.push(`/self-submitted-candidates/${candidateId}/show`);
    } else if (candidateId) {
      history.push(`/candidates/${candidateId}/show`);
    } else {
      history.push(ROUTES.CANDIDATES);
    }
  };

  const goBack = () => {
    const { description, ...touchedValues } = form.getFieldsValue(null, (meta: { touched?: boolean }) => meta.touched);
    if (description !== form.getFieldValue('description_initial') || Object.keys(touchedValues).length) {
      setOpenConfirmation(true);
    } else {
      onOkConfirmation();
    }
  };

  const onCloseConfirmation = () => {
    setOpenConfirmation(false);
    dispatch(setDuplicateCandidates([]));
  };

  const onOpenDuplicatesPopup = (data: Record<string, unknown>) => {
    setDuplicate(data);
    setOpenDuplicatePopup(true);
  };
  const onCloseDuplicationPopup = () => {
    setOpenDuplicatePopup(false);
    setDuplicate({});
  };

  const onValueChange = (
    { experience: exp, specialities: spec, country, state, city }: Partial<FormValues>,
    { experience, specialities }: FormValues
  ) => {
    if (exp) {
      form.setFieldValue(
        'experience',
        (experience || []).map((el, idx) => {
          const isFrom = !!exp[idx]?.worked_from;
          const isTill = !!exp[idx]?.worked_till;
          const worked_from = exp[idx]?.worked_from || el?.worked_from || moment();
          const worked_till = exp[idx]?.worked_till || el?.worked_till || moment();
          return {
            ...el,
            worked_from: isTill && worked_from.isAfter(worked_till) ? worked_till : worked_from,
            worked_till: isFrom && worked_till.isBefore(worked_from) ? worked_from : worked_till,
            company: el?.company?.slice(-1).map(c => ({ ...c, label: c.label || c.value })),
            position: el?.position?.slice(-1),
          };
        })
      );
    }
    if (spec) {
      form.setFieldValue(
        'specialities',
        (specialities || []).map(s =>
          s
            ? { level: s.level, speciality: s.speciality?.slice(-1) }
            : { speciality: undefined, level: { value: levels[0], label: levels[0] } }
        )
      );
    }
    if (country) {
      form.setFieldValue('state', undefined);
      form.setFieldValue('city', []);
    }
    if (state) {
      form.setFieldValue('city', []);
    }
    if (city) {
      form.setFieldValue('city', city.slice(-1));
    }
  };

  const onCheckDuplicates = useCallback(() => {
    const { first_name_latin, last_name_latin, first_name_local, last_name_local, phone, email } =
      form.getFieldsValue() as FormValues;
    const full_name_latin = `${first_name_latin || ''} ${last_name_latin || ''}`.trim();
    const full_name_local = `${first_name_local || ''} ${last_name_local || ''}`.trim();
    const duplicateData = { full_name_latin, full_name_local, phone, email };
    const checkDuplicate = props.checkDuplicate as unknown as (
      body: Record<string, unknown>
    ) => Promise<Array<unknown> | null | undefined>;
    checkDuplicate({
      full_name_latin,
      full_name_local,
      phone: (phone || []).map(encodeURIComponent),
      email: (email || []).map(encodeURIComponent),
      exclude_uuid: form.getFieldValue('uuid'),
    }).then(resp => {
      if (resp?.length) {
        notification({
          key: 'duplicates',
          type: NOTIFICATION_TYPES.WARNING,
          message: `Found ${resp.length} duplicates.`,
          btn: (
            <div role="none" onClick={() => onOpenDuplicatesPopup(duplicateData)}>
              Open Duplicates
            </div>
          ),
        });
      }
    });
  }, [form]);

  return (
    <div className="wrapper-content candidates-creation-page">
      <BreadCrumbs
        element={t('translation:candidate_creation.breadcrumb_candidates')}
        elementName={
          candidateId
            ? t('translation:candidate_creation.title_edit_candidate')
            : t('translation:candidate_creation.title_creation')
        }
        path="/candidates"
      />
      <Form
        form={form}
        onFinish={saveCandidate}
        initialValues={EMPTY_CANDIDATE_DATA}
        validateTrigger="onSubmit"
        scrollToFirstError
        onValuesChange={onValueChange}
      >
        <FormCreateEdit
          contentClassName="form-candidate-creation"
          title={
            candidateId
              ? t('translation:candidate_creation.title_edit_candidate')
              : t('translation:candidate_creation.title_creation')
          }
          options={SCROLL_BAR_CANDIDATE}
          onCancel={goBack}
          okText={
            candidateId
              ? t('translation:candidate_creation.button_save_changes')
              : t('translation:candidate_creation.button_create_candidate')
          }
          cancelText={t('translation:candidate_creation.button_cancel')}
          loading={loading}
        >
          <CandidateCreationForm
            form={form}
            onCheckDuplicates={onCheckDuplicates}
            isSSC={props.isSSC}
            setLoading={setLoading}
          />
        </FormCreateEdit>
        <Popup
          open={openConfirmation}
          title={t('translation:candidate_creation.title_discard_changes')}
          cancelButtonProps={{
            onClick: onCloseConfirmation,
            children: t('translation:candidate_creation.button_back_to_page'),
          }}
          okButtonProps={{
            onClick: onOkConfirmation,
            children: t('translation:candidate_creation.button_discard'),
          }}
        >
          {t('translation:candidate_creation.text_changes_not_saved')}
        </Popup>
        <PopupCandidateDuplicate
          open={openDuplicatePopup}
          onClose={onCloseDuplicationPopup}
          candidateDuplicates={props.candidateDuplicates as CandidateDuplicateEntry[]}
          duplicate={duplicate as unknown as CandidateDuplicateEntry}
        />
      </Form>
    </div>
  );
};

export default connector(CandidatesCreation);
